import { useCallback, useEffect, useState } from 'react';
import { errText, get, post } from '../api/client';
import { config } from '../api/config';
import { toastBad, toastOk } from './toast';

/** Web Push opt-in (docs/PORTAL.md, Notifications). `prefix`: '/push' for the team, '/portal/push' for a customer. */
type State = 'checking' | 'unsupported' | 'install' | 'blocked' | 'off' | 'on';

const b64uToBytes = (s: string) => {
  const t = s.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(t + '='.repeat((4 - (t.length % 4)) % 4)), (c) => c.charCodeAt(0));
};
const supported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof PushManager !== 'undefined' && typeof Notification !== 'undefined';
/** The one service worker (d8-mobile's) once it is running; a page without one never gets it. */
const ready = (ms = 4000) => Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), ms))]);
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

export function PushToggle({ prefix, label = 'Alerts', card = false }: { prefix: '/push' | '/portal/push'; label?: string; card?: boolean }) {
  const [state, setState] = useState<State>('checking');
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  const check = useCallback(async () => {
    // Safari on an iPhone has no push in a tab, only in the app added to the Home Screen: say so (when this server has push).
    const tab = !supported() && isIos() && !standalone();
    if (config.mock || (!supported() && !tab)) return setState('unsupported');
    try {
      const k = await get<{ publicKey: string | null }>(`${prefix}/key`);
      if (!k.publicKey) return setState('unsupported');          // this server has no push set up
    } catch { return setState('unsupported'); }
    if (tab) return setState('install');
    if (Notification.permission === 'denied') return setState('blocked');
    const reg = await ready();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    setState(sub && Notification.permission === 'granted' ? 'on' : 'off');
  }, [prefix]);
  useEffect(() => { void check(); }, [check]);

  if (state === 'checking' || state === 'unsupported') return null;
  const flip = async () => {
    setBusy(true);
    setHint(null);
    try {
      if (state === 'on') {
        const reg = await ready();
        const sub = await reg?.pushManager.getSubscription();
        if (sub) { await post(`${prefix}/unsubscribe`, { endpoint: sub.endpoint }); await sub.unsubscribe(); }
        setState('off');
        toastOk('Alerts are off on this device');
        return;
      }
      if (isIos() && !standalone()) { setHint('On an iPhone, add this page to your Home Screen first, then open it from there and turn alerts on.'); return; }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'blocked' : 'off'); return; }
      const reg = await ready();
      if (!reg) { setHint('Alerts need the installed app. Add it to your Home Screen, then try again.'); return; }
      const { publicKey } = await get<{ publicKey: string }>(`${prefix}/key`);
      const fresh = () => reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64uToBytes(publicKey) });
      const send = (s: PushSubscription) => { const j = s.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } }; return post(`${prefix}/subscribe`, { endpoint: j.endpoint, keys: j.keys }); };
      const sub = (await reg.pushManager.getSubscription()) ?? (await fresh());
      try {
        await send(sub);
      } catch (e) {
        // 409 endpoint_taken: this browser's address is another account's (someone else signed in here before). Drop it
        // and ask the browser for a new one, which is this person's alone.
        if ((e as { status?: number })?.status !== 409) throw e;
        await sub.unsubscribe();
        await send(await fresh());
      }
      setState('on');
      toastOk('Alerts are on for this device');
    } catch (e) {
      toastBad(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`flex min-h-[60px] items-center gap-3 ${card ? 'card mt-8 px-4 py-2' : ''}`} data-testid="push-toggle">
      <div className="min-w-0 flex-1">
        <b className="block text-[14.5px] font-medium">{label}</b>
        <span className="block text-[12.5px] text-dim">
          {state === 'install' ? 'On an iPhone, add this page to your Home Screen (Share, then Add to Home Screen), open it from there and turn alerts on.' : state === 'blocked' ? 'Blocked in this browser’s settings. Allow notifications for this site there, then come back.' : state === 'on' ? 'On for this device. You get a short alert, never the message itself.' : 'Get a short alert on this device. Your email choices above stay as they are.'}
        </span>
        {hint ? <span className="mt-1 block text-[12.5px] text-warn-text" role="status" data-testid="push-hint">{hint}</span> : null}
      </div>
      {state === 'blocked' || state === 'install' ? null : (
        <button role="switch" aria-checked={state === 'on'} aria-label={label} disabled={busy} onClick={() => void flip()} data-testid="push-switch"
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${state === 'on' ? 'bg-accent' : 'bg-line-3'}`}>
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-1 transition-all ${state === 'on' ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      )}
    </div>
  );
}
